import { createI18n } from 'vue-i18n/dist/vue-i18n.cjs';
import getUrlParameter from '@/helpers/get-url-parameter.js';
import styleguideTranslations from '@/styleguide/setup/styleguide.translations.json';
import numberFormats from './localization.json';

export const PAGE_LANG = document?.documentElement?.lang || 'en';
export const I18N_FALLBACK = 'en';
const pageLang = document?.documentElement?.lang || I18N_FALLBACK;
const showTranslationNotFoundError = false;

/**
 * Converts the given locale into the Pimcore desired format ('<lang>_<country>').
 *
 * @param {String} locale - The to be formatted locale.
 *
 * @returns {String}
 */
function formatLocale(locale) {
  return locale.split('-').join('_');
}

// TODO: currently not in use.
const locales = ['de', 'de-CH', 'en', 'en-US'];

export const I18N_LOCALES = import.meta.env.DEV ? locales : [];

const datetimeFormats = {
  // @see https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#parameter
  date: {
    // 00.00.0000
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
  dateShort: {
    // 00.00.00
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  },
  dateShortUTC: {
    // 00.00.00
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'utc',
  },
  dateAndTime: {
    // January 00, 0000, 00:00 PM
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
  dateAndTimeUTC: {
    // January 00, 0000, 00:00 PM
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'utc',
  },
  weekday: {
    // Monday, Tuesday, Wednesday, ...
    weekday: 'long',
  },
  weekdayNarrow: {
    // M, T, W, ...
    weekday: 'narrow',
  },
  month: {
    // January, February, March, ...
    month: 'long',
  },
  time: {
    // 00:00
    hour: '2-digit',
    minute: '2-digit',
  },
  timeUTC: {
    // 00:00
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'utc',
  },
};

const messages = {
  [I18N_FALLBACK]: {},
};

const options = {
  legacy: true,
  warnHtmlMessage: false, // https://vue-i18n.intlify.dev/api/composition#warnhtmlmessage
  locale: pageLang,
  fallbackLocale: I18N_FALLBACK,
  datetimeFormats: {
    [I18N_FALLBACK]: datetimeFormats,
    [pageLang]: datetimeFormats,
  },

  /**
   * Callback for the 'missing' event, during translation lookup.
   *
   * @param {String} locale - The current locale.
   * @param {String} messageKey - The request message key.
   */
  missing(locale, messageKey) {
    if (import.meta.env.DEV && /^s-language.*?/.test(messageKey)) {
      // Early exit for missing language selector messages.
      return;
    }

    if (import.meta.env.DEV && showTranslationNotFoundError) {
      console.error(`No '${locale}' translations found for '${messageKey}'`); // eslint-disable-line no-console
    }
  },
  messages,
  numberFormats,
};

const i18n = createI18n(options);

export default i18n;

/**
 * Merges the styleguide only translations into the messages object.
 *
 * TODO: check if this is still needed
 *
 * @param {String} locale - The required locale.
 *
 * @deprecated
 */
function addStyleguideTranslations(locale) {
  if (import.meta.env.DEV && styleguideTranslations[locale]) {
    i18n.global.mergeLocaleMessage(locale, styleguideTranslations[locale]);
  }
}

/**
 * Load messages for given locale if not already loaded.
 *
 * @param   {String}    locale    A locale identifier
 *
 * @returns {Promise}
 */
export const i18nLoadMessages = function (locale) {
  return Promise.resolve(locale);
};

/**
 * Sets the application locale to the given value.
 * Loads locale messages if needed.
 *
 * @param   {String}    locale    A locale identifier
 *
 * @returns {Promise}
 */
export const i18nSetLocale = function (locale) {
  if (i18n.global.locale !== locale) {
    return i18nLoadMessages(locale).then((newLocale) => {
      i18n.global.locale = newLocale;
    });
  }

  return Promise.resolve(locale);
};

/**
 * Returns the current locale as formatted string '<lang>_<country>'.
 *
 * @returns {String}
 */
export function getFormattedLocale() {
  return formatLocale(i18n.global.locale);
}

if (window.appTranslations) {
  messages[pageLang] = window.appTranslations;
  delete window.appTranslations;
} else {
  console.error(`No translations for locale '${pageLang}' available.`); // eslint-disable-line no-console
}

if (import.meta.env.DEV) {
  if (getUrlParameter('is_generator') === '1') {
    options.postTranslation = (message, key) => `{{ '${key}' | trans }}`;
  }

  /**
   * Filter certain missing translations.
   *
   * @type {RegExp}
   */
  options.silentTranslationWarn = /^s-language.*?/;

  /**
   * Hide fallback warnings.
   */
  options.silentFallbackWarn = true;

  addStyleguideTranslations(I18N_FALLBACK);

  i18nSetLocale(pageLang);
}
